/**
 * ChatDataCard
 * Renders rich data cards embedded in chat messages.
 * Dispatches to type-specific card layouts based on DataCard.type.
 */

import type { DataCard } from '@parlay-picker/shared/chatTypes';
import { ChevronRight } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { BackToBackCard } from './BackToBackCard';
import { ChatChartCard } from './ChatChartCard';
import { DirectionPriorCard } from './DirectionPriorCard';
import { DailyBriefingCard, ParlayComparisonCard, SettlementExplanationCard } from './HistoryCards';
import {
  AnalyticsSummaryCard,
  GenericCard,
  LeaderboardRankCard,
  LineMovementCard,
  NotificationListCard,
  TierComparisonCard,
} from './InfoCards';
import { LegAnalysisCard } from './LegAnalysisCards';
import { InjuryReportCard, LiveParlayCard } from './ParlayCards';
import { ParlaySummaryCard } from './ParlaySummaryCard';
import { PlayerPropHitRateCard } from './PlayerPropHitRateCard';
import { PlayerStatsCard } from './PlayerStatsCard';
import { TeamStatsCard } from './TeamStatsCard';

interface ChatDataCardProps {
  card: DataCard;
  onSendMessage?: (message: string) => void;
}

export function ChatDataCard({ card, onSendMessage }: ChatDataCardProps): ReactElement {
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Entry animation — fade/slide in
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const title = card.type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div
      ref={ref}
      className="transition-all duration-300 ease-out"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {/* Collapse toggle header */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-1 text-[10px] text-theme-text-secondary hover:text-theme-text mb-1 transition-colors"
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
        <span>{title}</span>
      </button>

      {!collapsed && <CardContent card={card} onSendMessage={onSendMessage} />}
    </div>
  );
}

function CardContent({ card, onSendMessage }: ChatDataCardProps): ReactElement {
  switch (card.type) {
    case 'player_stats':
      return <PlayerStatsCard data={card.data} />;
    case 'leg_analysis':
      return <LegAnalysisCard data={card.data} />;
    case 'parlay_summary':
      return <ParlaySummaryCard data={card.data} onSendMessage={onSendMessage} />;
    case 'injury_report':
      return <InjuryReportCard data={card.data} />;
    case 'live_parlay':
      return <LiveParlayCard data={card.data} />;
    case 'analytics_summary':
      return <AnalyticsSummaryCard data={card.data} />;
    case 'leaderboard_rank':
      return <LeaderboardRankCard data={card.data} />;
    case 'tier_comparison':
      return <TierComparisonCard data={card.data} />;
    case 'notification_list':
      return <NotificationListCard data={card.data} />;
    case 'line_movement':
      return <LineMovementCard data={card.data} />;
    case 'settlement_explanation':
      return <SettlementExplanationCard data={card.data} />;
    case 'parlay_comparison':
      return <ParlayComparisonCard data={card.data} />;
    case 'daily_briefing':
      return <DailyBriefingCard data={card.data} />;
    case 'team_stats':
      return <TeamStatsCard data={card.data} />;
    case 'back_to_back':
      return <BackToBackCard data={card.data} />;
    case 'player_prop_hit_rate':
      return <PlayerPropHitRateCard data={card.data} />;
    case 'direction_prior':
      return <DirectionPriorCard data={card.data} />;
    case 'chart':
      return <ChatChartCard data={card.data} onSendMessage={onSendMessage} />;
    default: {
      const _exhaustive: never = card;
      return (
        <GenericCard
          data={(card as { data: Record<string, unknown> }).data}
          type={(card as { type: string }).type}
        />
      );
    }
  }
}
